import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import GalleryGrid from "../components/GalleryGrid";
import SelectionSubmitForm from "../components/SelectionSubmitForm";
import { Lock, Download, Camera, CheckCircle, Play, X, ChevronLeft, ChevronRight, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API_URL = `${BACKEND_URL}/api`;

const PublicGalleryPage = () => {
  const { token } = useParams();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [sessionToken, setSessionToken] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Slideshow state
  const [slideshowActive, setSlideshowActive] = useState(false);
  const [slideshowIndex, setSlideshowIndex] = useState(0);

  // Comments state
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(null); // photo_id or null
  const [commentForm, setCommentForm] = useState({ author_name: "", text: "" });

  useEffect(() => {
    loadGallery();
  }, [token]);

  const loadGallery = async () => {
    try {
      const headers = {};
      if (sessionToken) headers["X-Gallery-Session"] = sessionToken;
      const res = await axios.get(`${API_URL}/gallery/${token}`, { headers });
      setGallery(res.data);
      setNeedsPassword(false);
    } catch (err) {
      if (err.response?.status === 401 && err.response?.data?.detail === "Password required") {
        setNeedsPassword(true);
      } else {
        setError(err.response?.data?.detail || "Galeria nie znaleziona");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async (photoId) => {
    try {
      const res = await axios.get(`${API_URL}/gallery/${token}/comments?photo_id=${photoId}`);
      setComments(res.data);
    } catch {}
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_URL}/gallery/${token}/verify-password`, { password });
      setSessionToken(res.data.session_token);
      setNeedsPassword(false);
      const galRes = await axios.get(`${API_URL}/gallery/${token}`, {
        headers: { "X-Gallery-Session": res.data.session_token },
      });
      setGallery(galRes.data);
    } catch {
      toast.error("Nieprawidlowe haslo");
    }
  };

  const toggleSelect = (photoId) => {
    setSelectedIds((prev) =>
      prev.includes(photoId) ? prev.filter((id) => id !== photoId) : [...prev, photoId]
    );
  };

  const handleSelectionSubmit = async (formData) => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/gallery/${token}/select`, {
        ...formData,
        selected_photo_ids: selectedIds,
      });
      setSubmitted(true);
      toast.success("Wybor zostal wyslany!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blad wysylania wyboru");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (photoId) => {
    try {
      const res = await axios.get(`${API_URL}/gallery/${token}/download/${photoId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = photoId;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Blad pobierania");
    }
  };

  const handleDownloadAll = async () => {
    try {
      toast.info("Przygotowywanie archiwum ZIP...");
      const res = await axios.get(`${API_URL}/gallery/${token}/download-all`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${gallery?.project_name || "gallery"}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Blad pobierania archiwum");
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!commentForm.author_name || !commentForm.text || !showComments) return;
    try {
      await axios.post(`${API_URL}/gallery/${token}/comments`, {
        author_name: commentForm.author_name,
        photo_id: showComments,
        text: commentForm.text,
      });
      setCommentForm((f) => ({ ...f, text: "" }));
      loadComments(showComments);
      toast.success("Komentarz dodany");
    } catch {
      toast.error("Blad dodawania komentarza");
    }
  };

  // Slideshow keyboard navigation
  const handleSlideshowKey = useCallback((e) => {
    if (!slideshowActive) return;
    const photos = gallery?.photos || [];
    if (e.key === "ArrowRight" || e.key === " ") {
      e.preventDefault();
      setSlideshowIndex((i) => (i + 1) % photos.length);
    } else if (e.key === "ArrowLeft") {
      setSlideshowIndex((i) => (i - 1 + photos.length) % photos.length);
    } else if (e.key === "Escape") {
      setSlideshowActive(false);
    }
  }, [slideshowActive, gallery]);

  useEffect(() => {
    window.addEventListener("keydown", handleSlideshowKey);
    return () => window.removeEventListener("keydown", handleSlideshowKey);
  }, [handleSlideshowKey]);

  // Auto-advance slideshow
  useEffect(() => {
    if (!slideshowActive) return;
    const timer = setInterval(() => {
      setSlideshowIndex((i) => (i + 1) % (gallery?.photos?.length || 1));
    }, 4000);
    return () => clearInterval(timer);
  }, [slideshowActive, gallery]);

  // Branding CSS vars
  const brandingStyle = gallery?.branding ? {
    "--brand-primary": gallery.branding.primary_color || "#4f46e5",
    "--brand-accent": gallery.branding.accent_color || "#ec4899",
    "--brand-bg": gallery.branding.background_color || "#0f172a",
  } : {};
  const bgColor = gallery?.branding?.background_color || "#0f172a";
  const primaryColor = gallery?.branding?.primary_color || "#4f46e5";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: bgColor }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center p-4" style={{ backgroundColor: bgColor }}>
        <div>
          <Camera className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Galeria niedostepna</h2>
          <p className="text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  if (needsPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: bgColor }}>
        <div className="w-full max-w-sm">
          <div className="text-center mb-6">
            {gallery?.branding?.logo_url && (
              <img src={gallery.branding.logo_url} alt="Logo" className="h-12 mx-auto mb-4" />
            )}
            <Lock className="w-12 h-12 text-slate-400 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-white">Galeria chroniona haslem</h2>
          </div>
          <form onSubmit={handlePasswordSubmit} className="space-y-3">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Haslo"
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:outline-none"
              style={{ "--tw-ring-color": primaryColor }}
              autoFocus
            />
            <button type="submit" className="w-full text-white py-3 rounded-lg font-medium transition-colors" style={{ backgroundColor: primaryColor }}>
              Otworz galerie
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center p-4" style={{ backgroundColor: bgColor }}>
        <div>
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Dziekujemy!</h2>
          <p className="text-slate-400">Twoj wybor ({selectedIds.length} zdjec) zostal wyslany do fotografa.</p>
        </div>
      </div>
    );
  }

  if (!gallery) return null;

  const photos = gallery.photos || [];
  const currentSlidePhoto = photos[slideshowIndex];

  return (
    <div className="min-h-screen" style={{ backgroundColor: bgColor, ...brandingStyle }}>
      {/* Header */}
      <div className="border-b border-slate-700 px-6 py-4" style={{ backgroundColor: `${bgColor}dd` }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {gallery.branding?.logo_url && (
              <img src={`${BACKEND_URL}${gallery.branding.logo_url}`} alt="Logo" className="h-8" />
            )}
            <div>
              <h1 className="text-xl font-bold text-white">
                {gallery.branding?.gallery_title || gallery.project_name}
              </h1>
              <p className="text-slate-400 text-sm">
                {gallery.photographer_name && `${gallery.photographer_name} · `}
                {photos.length} zdjec
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setSlideshowIndex(0); setSlideshowActive(true); }}
              className="text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors hover:opacity-90"
              style={{ backgroundColor: primaryColor }}
            >
              <Play className="w-4 h-4" /> Pokaz slajdow
            </button>
            {gallery.allow_downloads && (
              <button
                onClick={handleDownloadAll}
                className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
              >
                <Download className="w-4 h-4" /> Pobierz ZIP
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Gallery grid */}
      <div className="max-w-6xl mx-auto p-6 pb-24">
        <GalleryGrid
          photos={photos}
          watermarkText={gallery.watermark?.text}
          watermarkOpacity={gallery.watermark?.opacity}
          watermarkEnabled={!!gallery.watermark?.text}
          allowSelection={gallery.allow_selection}
          allowDownloads={gallery.allow_downloads}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onDownload={handleDownload}
          onComment={(photoId) => { setShowComments(photoId); loadComments(photoId); }}
          baseUrl={BACKEND_URL}
        />
      </div>

      {/* Selection submit bar */}
      {gallery.allow_selection && (
        <SelectionSubmitForm selectedCount={selectedIds.length} onSubmit={handleSelectionSubmit} loading={submitting} />
      )}

      {/* Slideshow overlay */}
      {slideshowActive && currentSlidePhoto && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center" onClick={() => setSlideshowActive(false)}>
          <button onClick={() => setSlideshowActive(false)} className="absolute top-4 right-4 text-white/60 hover:text-white z-10">
            <X className="w-8 h-8" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setSlideshowIndex((i) => (i - 1 + photos.length) % photos.length); }}
            className="absolute left-4 text-white/60 hover:text-white z-10"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setSlideshowIndex((i) => (i + 1) % photos.length); }}
            className="absolute right-4 text-white/60 hover:text-white z-10"
          >
            <ChevronRight className="w-10 h-10" />
          </button>
          <img
            src={currentSlidePhoto.url?.startsWith("http") ? currentSlidePhoto.url : `${BACKEND_URL}${currentSlidePhoto.url}`}
            alt={currentSlidePhoto.filename}
            className="max-h-[90vh] max-w-[90vw] object-contain transition-opacity duration-500"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            {slideshowIndex + 1} / {photos.length}
          </div>
        </div>
      )}

      {/* Comments panel */}
      {showComments && (
        <div className="fixed inset-y-0 right-0 w-80 bg-white shadow-xl z-40 flex flex-col">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <MessageCircle className="w-4 h-4" /> Komentarze
            </h3>
            <button onClick={() => setShowComments(null)} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {comments.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Brak komentarzy</p>}
            {comments.map((c) => (
              <div key={c.id} className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-900">{c.author_name}</span>
                  <span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleDateString("pl-PL")}</span>
                </div>
                <p className="text-sm text-slate-600">{c.text}</p>
              </div>
            ))}
          </div>
          <form onSubmit={handleCommentSubmit} className="p-4 border-t space-y-2">
            <input
              type="text"
              placeholder="Twoje imie"
              value={commentForm.author_name}
              onChange={(e) => setCommentForm((f) => ({ ...f, author_name: e.target.value }))}
              required
              className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            />
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Napisz komentarz..."
                value={commentForm.text}
                onChange={(e) => setCommentForm((f) => ({ ...f, text: e.target.value }))}
                required
                className="flex-1 border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              />
              <button type="submit" className="bg-indigo-600 text-white p-2 rounded-lg hover:bg-indigo-700">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Powered by badge */}
      {!gallery.branding?.hide_storybook_badge && (
        <div className="fixed bottom-4 left-4 text-slate-500 text-xs z-30">
          Powered by StoryBook
        </div>
      )}
    </div>
  );
};

export default PublicGalleryPage;
